import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useKiosk } from '../hooks/useKiosk';
import { AVATARS } from '../components/JoinScreen';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;
const MAX_VISIBLE = 10;

function avatarEmoji(avatarId) {
  return AVATARS.find((a) => a.id === avatarId)?.emoji || '👤';
}

function formatWait(seconds, t) {
  if (!seconds || seconds <= 0) return '--';
  const min = Math.ceil(seconds / 60);
  return t('kiosk.waitShort', { min });
}

export default function KioskPage() {
  const { t } = useTranslation();
  const { shopId } = useParams();
  const [searchParams] = useSearchParams();
  const orientation = searchParams.get('orientation') === 'portrait' ? 'portrait' : 'landscape';
  const {
    queues, entries, calledTicket, wsStatus,
    session, autoRegisterIfAdmin, redeemPairingCode,
    issueWalkUpTicket, lastIssuedTicket, issueError, clearLastIssuedTicket,
  } = useKiosk(shopId);
  const [pairingCode, setPairingCode] = useState('');
  const [pairingError, setPairingError] = useState(null);
  const [pairingBusy, setPairingBusy] = useState(false);
  const [shopName, setShopName] = useState('');
  const [kioskHeadline, setKioskHeadline] = useState('');
  const [kioskSubtext, setKioskSubtext] = useState('');
  const [services, setServices] = useState([]);
  const [time, setTime] = useState(new Date());
  const [dynamicQrEnabled, setDynamicQrEnabled] = useState(false);
  const [walkUpEnabled, setWalkUpEnabled] = useState(true);
  const [walkUpDisplayName, setWalkUpDisplayName] = useState('');
  const [overlayDurationSeconds, setOverlayDurationSeconds] = useState(45);
  const [acceptingNewCustomers, setAcceptingNewCustomers] = useState(true);
  const [qrToken, setQrToken] = useState('');
  const [qrCountdown, setQrCountdown] = useState(25);
  const [showServicePicker, setShowServicePicker] = useState(false);
  const [overlayRemaining, setOverlayRemaining] = useState(0);
  const [issueInFlight, setIssueInFlight] = useState(false);
  const [registrationAttempted, setRegistrationAttempted] = useState(false);
  // Frontend-only error code for issuance attempts that fail before
  // reaching the server (e.g. WebSocket not connected). Merged with
  // server-side issueError in the toast below.
  const [localIssueError, setLocalIssueError] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Auto-register this kiosk if nobody has done it yet. Works if an admin
  // is logged in in the same browser (typical "I clicked Preview from
  // Settings" flow) — their JWT is used to call the protected endpoint.
  // On a truly un-paired tablet there's no admin JWT; the WS handshake
  // will be rejected and the walk-up button will show an error telling
  // the admin to register the kiosk from the Settings page first.
  useEffect(() => {
    if (!shopId || registrationAttempted) return;
    if (session?.deviceId && session?.token) return;
    autoRegisterIfAdmin().finally(() => setRegistrationAttempted(true));
  }, [shopId, session, autoRegisterIfAdmin, registrationAttempted]);

  useEffect(() => {
    if (!shopId) return;
    fetch(`${API_BASE}/api/v1/public/shops/by-id/${shopId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) {
          setShopName(d.name);
          if (d.kioskHeadline) setKioskHeadline(d.kioskHeadline);
          if (d.kioskSubtext) setKioskSubtext(d.kioskSubtext);
          setDynamicQrEnabled(!!d.dynamicQrEnabled);
          setWalkUpEnabled(d.walkUpTicketEnabled !== false);
          if (d.walkUpDisplayName) setWalkUpDisplayName(d.walkUpDisplayName);
          if (typeof d.walkUpOverlayDurationSeconds === 'number' && d.walkUpOverlayDurationSeconds > 0) {
            setOverlayDurationSeconds(d.walkUpOverlayDurationSeconds);
          }
          setAcceptingNewCustomers(d.acceptingNewCustomers !== false);
        }
      })
      .catch(() => {});
    fetch(`${API_BASE}/api/v1/public/shops/${shopId}/services`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => setServices(Array.isArray(list) ? list : []))
      .catch(() => {});
  }, [shopId]);

  // Dynamic QR token rotation. Minting now requires the kiosk device
  // credentials — the server only issues a token to the shop's own
  // registered kiosk, which is what makes a scanned code proof of physical
  // presence. A read-only display without a device session can't mint.
  useEffect(() => {
    if (!dynamicQrEnabled || !shopId) return;
    if (!session?.deviceId || !session?.token) return;
    const fetchToken = () => {
      const params = new URLSearchParams({ deviceId: session.deviceId, deviceToken: session.token });
      fetch(`${API_BASE}/api/v1/public/shops/${shopId}/qr-token?${params.toString()}`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => { if (d) { setQrToken(d.token); setQrCountdown(25); } })
        .catch(() => {});
    };
    fetchToken();
    const id = setInterval(fetchToken, 25000);
    return () => clearInterval(id);
  }, [dynamicQrEnabled, shopId, session]);

  // Countdown ticker
  useEffect(() => {
    if (!dynamicQrEnabled) return;
    const id = setInterval(() => setQrCountdown((c) => Math.max(c - 1, 0)), 1000);
    return () => clearInterval(id);
  }, [dynamicQrEnabled]);

  // Clock tick
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Overlay auto-dismiss countdown — starts when a ticket is issued
  useEffect(() => {
    if (!lastIssuedTicket) {
      setOverlayRemaining(0);
      return;
    }
    setOverlayRemaining(overlayDurationSeconds);
    const id = setInterval(() => {
      setOverlayRemaining((r) => {
        if (r <= 1) {
          clearLastIssuedTicket();
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [lastIssuedTicket, overlayDurationSeconds, clearLastIssuedTicket]);

  // 15s cooldown after a successful issue or a RATE_LIMITED response
  const COOLDOWN_SECONDS = 15;
  useEffect(() => {
    if (lastIssuedTicket || issueError === 'RATE_LIMITED') {
      setCooldownRemaining(COOLDOWN_SECONDS);
    }
  }, [lastIssuedTicket, issueError]);

  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const id = setInterval(() => {
      setCooldownRemaining((r) => Math.max(r - 1, 0));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownRemaining > 0]);

  // Auto-dismiss error toast after 5 seconds
  useEffect(() => {
    const err = issueError || localIssueError;
    if (!err) return;
    const id = setTimeout(() => {
      clearLastIssuedTicket();
      setLocalIssueError(null);
    }, 5000);
    return () => clearTimeout(id);
  }, [issueError, localIssueError, clearLastIssuedTicket]);

  const handleWalkUpClick = async () => {
    if (issueInFlight) return;
    if (!session?.deviceId) {
      setLocalIssueError('KIOSK_NOT_REGISTERED');
      return;
    }
    if (services.length > 1) {
      setShowServicePicker(true);
      return;
    }
    await triggerIssue(services[0]?.id || '');
  };

  const triggerIssue = async (serviceId) => {
    setShowServicePicker(false);
    if (issueInFlight) return;
    setIssueInFlight(true);
    try {
      await issueWalkUpTicket({ serviceId });
    } catch (e) {
      // Surface the error to the user instead of silently failing.
      // Most common case: the kiosk isn't paired so the WS handshake
      // was rejected — the hook throws "Kiosk WebSocket not connected".
      console.error('Walk-up issue failed', e);
      const code = wsStatus !== 'connected' ? 'NOT_CONNECTED' : 'KIOSK_ISSUE_FAILED';
      setLocalIssueError(code);
    } finally {
      // A tiny delay lets the WS response land before the user can re-tap
      setTimeout(() => setIssueInFlight(false), 1500);
    }
  };

  const entryDisplayName = (entry) =>
    entry.firstName || walkUpDisplayName || t('kiosk.walkInTicket');

  const baseQrUrl = `${window.location.origin}/q/${shopId}`;
  const qrUrl = dynamicQrEnabled && qrToken ? `${baseQrUrl}?t=${qrToken}` : baseQrUrl;
  const totalCount = Object.values(queues).reduce((sum, q) => sum + q.length, 0);
  const serviceMap = Object.fromEntries(services.map((s) => [s.id, s.name]));
  const queueKeys = Object.keys(queues);
  const hasMultipleQueues = queueKeys.length > 1 || (queueKeys.length === 1 && queueKeys[0] !== '_default');
  // "Now serving" is driven solely by the CallNextEvent — never by matching
  // against the queue. The kiosk gets an anonymized copy where every entry's
  // ticketUuid is blank, so an entries.find(by ticketUuid) would wrongly match
  // the first queue member (e.g. a just-issued walk-up) even though nobody was
  // called. The CallNextEvent already carries the number/avatar we display.
  const calledEntry = calledTicket
    ? {
        firstName: calledTicket.firstName || '',
        avatarId: calledTicket.avatarId || '',
        ticketNumber: calledTicket.ticketNumber || null,
      }
    : null;
  // Highlight the called row by ticket NUMBER (public, non-sensitive) rather
  // than the blanked ticketUuid, which would otherwise match every row.
  const calledNumber = calledTicket?.ticketNumber || null;

  return (
    <>
      <style>{CSS}</style>
      <div className={`kiosk kiosk-${orientation}`}>
        {/* Pairing banner — shown on an un-paired tablet (no admin session in
            this browser). The admin generates a code in Settings → Offices and
            enters it here; no admin credential ever reaches this device. */}
        {registrationAttempted && !session?.deviceId && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
            padding: '0.6rem 1rem', background: '#0B1220', borderBottom: '1px solid #334155',
          }}>
            <span style={{ color: '#94A3B8', fontSize: '0.85rem', fontWeight: 600 }}>
              {t('kiosk.pairPrompt', 'Pair this device:')}
            </span>
            <input
              value={pairingCode}
              onChange={(e) => { setPairingCode(e.target.value.toUpperCase()); setPairingError(null); }}
              placeholder={t('kiosk.pairCodePlaceholder', 'Enter code')}
              aria-label={t('kiosk.pairCodePlaceholder', 'Enter code')}
              maxLength={12}
              style={{
                padding: '0.4rem 0.6rem', borderRadius: 6, border: '1px solid #475569',
                background: '#020617', color: '#F8FAFC', letterSpacing: '0.15em',
                fontFamily: 'monospace', fontSize: '1rem', width: 140, textTransform: 'uppercase',
              }}
            />
            <button
              disabled={pairingBusy || !pairingCode.trim()}
              onClick={async () => {
                setPairingBusy(true);
                setPairingError(null);
                try {
                  await redeemPairingCode(pairingCode.trim());
                  setPairingCode('');
                } catch (err) {
                  setPairingError(err.status === 429
                    ? t('kiosk.pairRateLimited', 'Too many attempts — wait a moment')
                    : t('kiosk.pairInvalid', 'Invalid or expired code'));
                } finally {
                  setPairingBusy(false);
                }
              }}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: '#2563EB', color: '#fff', fontWeight: 700, opacity: pairingBusy ? 0.6 : 1,
              }}
            >
              {pairingBusy ? '…' : t('kiosk.pairButton', 'Pair')}
            </button>
            {pairingError && (
              <span style={{ color: '#F87171', fontSize: '0.8rem' }}>{pairingError}</span>
            )}
          </div>
        )}
        {/* Header */}
        <header className="kiosk-header">
          <div className="kiosk-brand">
            <span className="kiosk-logo">vQueue</span>
            <span className="kiosk-shop">{shopName}</span>
          </div>
          <div className="kiosk-meta">
            <span className="kiosk-time">
              {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className={`kiosk-status ${wsStatus === 'connected' ? 'online' : 'offline'}`}>
              {wsStatus === 'connected' ? t('kiosk.live') : t('kiosk.connecting')}
            </span>
          </div>
        </header>

        {/* Now Serving Banner */}
        {calledEntry && (
          <div className="kiosk-now-serving">
            <span className="kiosk-now-label">{t('kiosk.nowServing')}</span>
            <span className="kiosk-now-ticket">#{calledEntry.ticketNumber || '—'}</span>
            <span className="kiosk-now-name">
              {avatarEmoji(calledEntry.avatarId)} {calledEntry.firstName || walkUpDisplayName || t('kiosk.walkInTicket')}
            </span>
            {calledTicket?.serviceId && serviceMap[calledTicket.serviceId] && (
              <span className="kiosk-now-service">{serviceMap[calledTicket.serviceId]}</span>
            )}
          </div>
        )}

        {/* Main content */}
        <div className="kiosk-body">
          {/* QR Code side */}
          <div className="kiosk-qr-panel">
            <div className="kiosk-qr-box">
              <img
                key={qrUrl}
                src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(qrUrl)}&_=${Date.now()}`}
                alt="Scan to join queue"
                className="kiosk-qr-img"
              />
            </div>
            <p className="kiosk-qr-hint">{kioskHeadline || t('kiosk.scanToJoin')}</p>
            <p className="kiosk-qr-sub">{kioskSubtext || t('kiosk.noAppNeeded')}</p>
            {dynamicQrEnabled && (
              <div className="kiosk-qr-timer">
                <div className="kiosk-qr-timer-bar" style={{ width: `${(qrCountdown / 25) * 100}%` }} />
              </div>
            )}

            {walkUpEnabled && (
              <>
                <div className="kiosk-divider"><span>{t('kiosk.or')}</span></div>
                <button
                  type="button"
                  className="kiosk-walkup-btn"
                  onClick={handleWalkUpClick}
                  disabled={issueInFlight || !acceptingNewCustomers || cooldownRemaining > 0}
                  aria-label={t('kiosk.walkUp.button')}
                >
                  <span className="kiosk-walkup-icon" aria-hidden="true">🎫</span>
                  <span>
                    {!acceptingNewCustomers
                      ? t('kiosk.walkUp.closed')
                      : cooldownRemaining > 0
                        ? t('kiosk.walkUp.cooldown', { seconds: cooldownRemaining })
                        : t('kiosk.walkUp.button')}
                  </span>
                  {cooldownRemaining > 0 && (
                    <span className="kiosk-cooldown-bar">
                      <span
                        className="kiosk-cooldown-fill"
                        style={{ width: `${(cooldownRemaining / COOLDOWN_SECONDS) * 100}%` }}
                      />
                    </span>
                  )}
                </button>
              </>
            )}
          </div>

          {/* Queue list side */}
          <div className="kiosk-queue-panel">
            <div className="kiosk-queue-header">
              <span>{t('kiosk.queueHeader')}</span>
              <span className="kiosk-queue-count">
                {totalCount} {totalCount === 1 ? t('kiosk.person') : t('kiosk.people')}
              </span>
            </div>

            {totalCount === 0 ? (
              <div className="kiosk-empty">
                <span style={{ fontSize: '3rem' }}>🎉</span>
                <p>{t('kiosk.emptyQueue')}</p>
              </div>
            ) : hasMultipleQueues ? (() => {
              const activeQueues = queueKeys.filter((k) => (queues[k] || []).length > 0);
              const perQueue = activeQueues.length >= 4 ? 3 : activeQueues.length >= 2 ? 5 : MAX_VISIBLE;
              const compact = activeQueues.length >= 3;
              return (
                <div className={`kiosk-list ${compact ? 'kiosk-list-compact' : ''} ${activeQueues.length >= 3 ? 'kiosk-list-grid' : ''}`}>
                  {activeQueues.map((svcId) => {
                    const svcEntries = queues[svcId] || [];
                    const svcName = serviceMap[svcId] || svcId;
                    const svcVisible = svcEntries.slice(0, perQueue);
                    const remaining = svcEntries.length - svcVisible.length;
                    return (
                      <div key={svcId} className="kiosk-service-section">
                        <div className="kiosk-service-header">
                          <span>{svcName}</span>
                          <span className="kiosk-service-count">{svcEntries.length}</span>
                        </div>
                        {svcVisible.map((entry, idx) => (
                          <div
                            key={entry.ticketNumber || entry.position || idx}
                            className={`kiosk-entry ${calledNumber && calledNumber === entry.ticketNumber ? 'kiosk-entry-called' : ''}`}
                          >
                            <span className="kiosk-pos">#{entry.ticketNumber || entry.position}</span>
                            <span className="kiosk-avatar">{avatarEmoji(entry.avatarId)}</span>
                            <span className="kiosk-name">{entryDisplayName(entry)}</span>
                            {!compact && <span className="kiosk-wait">{formatWait(entry.estimatedWaitSeconds, t)}</span>}
                          </div>
                        ))}
                        {remaining > 0 && (
                          <div className="kiosk-more">+{remaining} {t('kiosk.moreInQueue')}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })() : (
              <div className="kiosk-list">
                {(queues[queueKeys[0]] || []).slice(0, MAX_VISIBLE).map((entry, idx) => (
                  <div
                    key={entry.ticketNumber || entry.position || idx}
                    className={`kiosk-entry ${calledNumber && calledNumber === entry.ticketNumber ? 'kiosk-entry-called' : ''}`}
                  >
                    <span className="kiosk-pos">#{entry.ticketNumber || entry.position}</span>
                    <span className="kiosk-avatar">{avatarEmoji(entry.avatarId)}</span>
                    <span className="kiosk-name">{entryDisplayName(entry)}</span>
                    <span className="kiosk-wait">{formatWait(entry.estimatedWaitSeconds, t)}</span>
                  </div>
                ))}
                {(queues[queueKeys[0]] || []).length > MAX_VISIBLE && (
                  <div className="kiosk-more">+{(queues[queueKeys[0]] || []).length - MAX_VISIBLE} {t('kiosk.moreInQueue')}</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Service picker (only when shop has multiple services) */}
        {showServicePicker && (
          <div className="kiosk-modal-backdrop" role="dialog" aria-modal="true">
            <div className="kiosk-modal">
              <h2 className="kiosk-modal-title">{t('kiosk.walkUp.pickService')}</h2>
              <div className="kiosk-service-picker">
                {services.map((svc) => (
                  <button
                    key={svc.id}
                    type="button"
                    className="kiosk-service-option"
                    onClick={() => triggerIssue(svc.id)}
                    disabled={issueInFlight}
                  >
                    {svc.name}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="kiosk-modal-cancel"
                onClick={() => setShowServicePicker(false)}
              >
                {t('kiosk.walkUp.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Issued ticket overlay */}
        {lastIssuedTicket && (
          <div className="kiosk-modal-backdrop" role="dialog" aria-modal="true" aria-live="polite">
            <div className="kiosk-ticket-overlay">
              <p className="kiosk-ticket-label">{t('kiosk.walkUp.yourNumber')}</p>
              <div className="kiosk-ticket-number">#{lastIssuedTicket.ticketNumber}</div>
              <p className="kiosk-ticket-instruction">{t('kiosk.walkUp.instruction')}</p>
              <div className="kiosk-ticket-timer" aria-hidden="true">
                <div
                  className="kiosk-ticket-timer-bar"
                  style={{ width: `${Math.max(0, (overlayRemaining / overlayDurationSeconds) * 100)}%` }}
                />
              </div>
              <p className="kiosk-ticket-countdown">
                {t('kiosk.walkUp.dismissing', { seconds: overlayRemaining })}
              </p>
              <button
                type="button"
                className="kiosk-ticket-done"
                onClick={clearLastIssuedTicket}
              >
                {t('kiosk.walkUp.done')}
              </button>
            </div>
          </div>
        )}

        {/* Transient error banner — merges server (issueError) and
            local (localIssueError, e.g. "not connected") codes. */}
        {(issueError || localIssueError) && !lastIssuedTicket && (
          <div
            className="kiosk-error-toast"
            role="status"
            onClick={() => { clearLastIssuedTicket(); setLocalIssueError(null); }}
          >
            {(issueError || localIssueError) === 'RATE_LIMITED'
              ? t('kiosk.walkUp.rateLimited')
              : (issueError || localIssueError) === 'NOT_CONNECTED'
                ? t('kiosk.walkUp.notConnected')
                : (issueError || localIssueError) === 'UNAUTHORIZED' || (issueError || localIssueError) === 'KIOSK_NOT_REGISTERED'
                  ? t('kiosk.walkUp.notRegistered')
                  : (issueError || localIssueError) === 'OUTSIDE_BUSINESS_HOURS'
                    ? t('kiosk.walkUp.outsideHours')
                    : (issueError || localIssueError) === 'QUEUE_CLOSED'
                      ? t('kiosk.walkUp.queueClosed')
                      : t('kiosk.walkUp.error')}
          </div>
        )}
      </div>
    </>
  );
}

const CSS = `
  .kiosk {
    min-height: 100dvh; background: #0F172A; color: #F8FAFC;
    font-family: 'Inter', system-ui, sans-serif;
    display: flex; flex-direction: column;
  }
  .kiosk-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 1rem 2rem; border-bottom: 1px solid #334155;
  }
  .kiosk-brand { display: flex; align-items: center; gap: 1rem; }
  .kiosk-logo { font-size: 1.5rem; font-weight: 800; background: linear-gradient(135deg, #7C3AED, #06B6D4); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
  .kiosk-shop { font-size: 1.1rem; font-weight: 600; color: #CBD5E1; }
  .kiosk-meta { display: flex; align-items: center; gap: 1rem; }
  .kiosk-time { font-size: 1.5rem; font-weight: 700; color: #E2E8F0; font-variant-numeric: tabular-nums; }
  .kiosk-status { font-size: 0.75rem; font-weight: 700; padding: 4px 10px; border-radius: 20px; letter-spacing: 0.05em; }
  .kiosk-status.online { background: #34D399; color: #0F172A; }
  .kiosk-status.offline { background: #F59E0B; color: #0F172A; }

  .kiosk-now-serving {
    background: linear-gradient(90deg, #7C3AED, #06B6D4);
    padding: 1rem 2rem; display: flex; align-items: center; gap: 1rem;
    animation: pulse-bg 2s ease-in-out infinite;
  }
  .kiosk-now-label { font-size: 0.85rem; font-weight: 800; letter-spacing: 0.1em; color: rgba(0,0,0,0.6); }
  .kiosk-now-ticket { font-size: 2rem; font-weight: 900; color: #fff; font-variant-numeric: tabular-nums; }
  .kiosk-now-name { font-size: 1.75rem; font-weight: 800; color: #fff; }
  .kiosk-now-service {
    margin-left: auto; font-size: 0.8rem; font-weight: 700; color: rgba(255,255,255,0.85);
    background: rgba(0,0,0,0.2); padding: 4px 12px; border-radius: 12px;
  }
  @keyframes pulse-bg { 0%,100% { opacity: 1; } 50% { opacity: 0.85; } }

  .kiosk-body { flex: 1; display: flex; padding: 2rem; gap: 2rem; }
  .kiosk-portrait .kiosk-body { flex-direction: column; }
  .kiosk-portrait .kiosk-qr-panel { flex: 0 0 auto; }
  .kiosk-portrait .kiosk-qr-img { width: 240px; height: 240px; }
  .kiosk-portrait .kiosk-queue-panel { flex: 1; min-height: 0; }

  .kiosk-qr-panel {
    flex: 0 0 340px; display: flex; flex-direction: column; align-items: center;
    justify-content: center; gap: 1rem;
  }
  .kiosk-qr-box {
    background: #fff; border-radius: 16px; padding: 1.5rem;
    box-shadow: 0 0 40px rgba(6,182,212,0.15);
  }
  .kiosk-qr-img { display: block; width: 280px; height: 280px; }
  .kiosk-qr-hint { font-size: 1.1rem; font-weight: 700; color: #E2E8F0; margin: 0; }
  .kiosk-qr-sub { font-size: 0.85rem; color: #94A3B8; margin: 0; }
  .kiosk-qr-timer { width: 200px; height: 4px; background: #334155; border-radius: 2px; margin-top: 10px; overflow: hidden; }
  .kiosk-qr-timer-bar { height: 100%; background: linear-gradient(90deg, #7C3AED, #06B6D4); border-radius: 2px; transition: width 1s linear; }

  .kiosk-queue-panel { flex: 1; display: flex; flex-direction: column; min-height: 0; overflow-y: auto; }
  .kiosk-queue-header {
    display: flex; justify-content: space-between; align-items: center;
    padding-bottom: 1rem; border-bottom: 1px solid #334155;
    font-size: 1.25rem; font-weight: 700;
  }
  .kiosk-queue-count { font-size: 0.9rem; font-weight: 500; color: #94A3B8; }

  .kiosk-empty {
    flex: 1; display: flex; flex-direction: column; align-items: center;
    justify-content: center; gap: 0.5rem; color: #94A3B8; font-size: 1.1rem;
  }

  .kiosk-list { display: flex; flex-direction: column; gap: 0.5rem; margin-top: 1rem; }
  .kiosk-entry {
    display: flex; align-items: center; gap: 1rem; padding: 0.85rem 1rem;
    background: #1E293B; border-radius: 10px; border: 1px solid #334155; transition: background 0.2s;
  }
  .kiosk-entry-called { background: rgba(124,58,237,0.12); border: 2px solid #7C3AED; }
  .kiosk-pos { font-size: 1.1rem; font-weight: 800; color: #06B6D4; min-width: 40px; }
  .kiosk-avatar { font-size: 1.5rem; }
  .kiosk-name { flex: 1; font-size: 1.1rem; font-weight: 600; }
  .kiosk-wait { font-size: 0.9rem; color: #94A3B8; font-variant-numeric: tabular-nums; }
  .kiosk-more { text-align: center; color: #94A3B8; font-size: 0.9rem; padding: 0.5rem; }

  .kiosk-list-compact .kiosk-entry { padding: 0.5rem 0.75rem; gap: 0.5rem; }
  .kiosk-list-compact .kiosk-pos { font-size: 0.95rem; min-width: 32px; }
  .kiosk-list-compact .kiosk-avatar { font-size: 1.1rem; }
  .kiosk-list-compact .kiosk-name { font-size: 0.95rem; }
  .kiosk-list-grid {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem;
  }
  .kiosk-list-grid .kiosk-service-section { margin-bottom: 0; }

  .kiosk-service-section { margin-bottom: 1rem; }
  .kiosk-service-section:last-child { margin-bottom: 0; }
  .kiosk-service-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 0.5rem 0; margin-bottom: 0.25rem;
    font-size: 0.95rem; font-weight: 700; color: #7C3AED;
    border-bottom: 1px solid #334155;
  }
  .kiosk-service-count {
    font-size: 0.8rem; font-weight: 500; color: #94A3B8;
    background: #334155; padding: 2px 8px; border-radius: 10px;
  }

  @media (max-width: 768px) {
    .kiosk-body { flex-direction: column; padding: 1rem; }
    .kiosk-qr-panel { flex: 0 0 auto; }
    .kiosk-qr-img { width: 200px; height: 200px; }
  }

  /* Walk-up no-phone button */
  .kiosk-divider {
    width: 100%; max-width: 300px; display: flex; align-items: center;
    gap: 0.75rem; color: #64748B; font-size: 0.75rem; font-weight: 700;
    letter-spacing: 0.15em; margin-top: 0.75rem;
  }
  .kiosk-divider::before, .kiosk-divider::after {
    content: ''; flex: 1; height: 1px; background: #334155;
  }
  .kiosk-walkup-btn {
    width: 100%; max-width: 300px; padding: 1.1rem 1.25rem;
    display: flex; align-items: center; justify-content: center; gap: 0.75rem;
    background: #1E293B; color: #F8FAFC; border: 2px solid #334155;
    border-radius: 14px; font-size: 1.05rem; font-weight: 700; cursor: pointer;
    transition: transform 0.1s, border-color 0.2s, background 0.2s;
    min-height: 64px;
  }
  .kiosk-walkup-btn:hover:not(:disabled) { background: #334155; border-color: #7C3AED; }
  .kiosk-walkup-btn:active:not(:disabled) { transform: scale(0.98); }
  .kiosk-walkup-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .kiosk-walkup-btn { position: relative; overflow: hidden; }
  .kiosk-cooldown-bar {
    position: absolute; bottom: 0; left: 0; right: 0; height: 4px;
    background: #334155;
  }
  .kiosk-cooldown-fill {
    display: block; height: 100%;
    background: linear-gradient(90deg, #7C3AED, #06B6D4);
    border-radius: 2px; transition: width 1s linear;
  }
  .kiosk-walkup-icon { font-size: 1.5rem; }

  /* Modal / overlay */
  .kiosk-modal-backdrop {
    position: fixed; inset: 0; background: rgba(15, 23, 42, 0.92);
    display: flex; align-items: center; justify-content: center;
    z-index: 100; padding: 2rem;
  }
  .kiosk-modal {
    background: #1E293B; border: 1px solid #334155; border-radius: 20px;
    padding: 2rem; max-width: 600px; width: 100%; text-align: center;
  }
  .kiosk-modal-title { margin: 0 0 1.5rem 0; color: #F8FAFC; font-size: 1.5rem; }
  .kiosk-service-picker {
    display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 1.5rem;
  }
  @media (max-width: 480px) { .kiosk-service-picker { grid-template-columns: 1fr; } }
  .kiosk-service-option {
    padding: 1.25rem; background: #0F172A; border: 2px solid #334155;
    border-radius: 12px; color: #F8FAFC; font-size: 1.05rem; font-weight: 600;
    cursor: pointer; min-height: 64px; transition: border-color 0.2s, background 0.2s;
  }
  .kiosk-service-option:hover:not(:disabled) { border-color: #7C3AED; background: #1E293B; }
  .kiosk-service-option:disabled { opacity: 0.5; cursor: not-allowed; }
  .kiosk-modal-cancel {
    padding: 0.75rem 1.5rem; background: transparent; border: 1px solid #475569;
    border-radius: 10px; color: #94A3B8; font-size: 0.95rem; cursor: pointer;
  }
  .kiosk-modal-cancel:hover { background: #0F172A; color: #F8FAFC; }

  .kiosk-ticket-overlay {
    background: #1E293B; border: 2px solid #7C3AED; border-radius: 24px;
    padding: 3rem 2rem; max-width: 640px; width: 100%; text-align: center;
    box-shadow: 0 0 80px rgba(124,58,237,0.3);
  }
  .kiosk-ticket-label {
    margin: 0; color: #94A3B8; font-size: 1rem; font-weight: 700;
    letter-spacing: 0.2em; text-transform: uppercase;
  }
  .kiosk-ticket-number {
    font-size: clamp(8rem, 20vw, 14rem); font-weight: 900;
    background: linear-gradient(135deg, #7C3AED, #06B6D4);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    line-height: 1; margin: 1rem 0; font-variant-numeric: tabular-nums;
  }
  .kiosk-ticket-instruction {
    font-size: 1.15rem; color: #CBD5E1; margin: 0 0 2rem 0; line-height: 1.5;
  }
  .kiosk-ticket-timer {
    width: 100%; height: 6px; background: #334155; border-radius: 3px;
    margin-bottom: 0.75rem; overflow: hidden;
  }
  .kiosk-ticket-timer-bar {
    height: 100%; background: linear-gradient(90deg, #7C3AED, #06B6D4);
    border-radius: 3px; transition: width 1s linear;
  }
  .kiosk-ticket-countdown {
    font-size: 0.9rem; color: #94A3B8; margin: 0 0 1.5rem 0;
    font-variant-numeric: tabular-nums;
  }
  .kiosk-ticket-done {
    padding: 1rem 3rem; background: #7C3AED; color: #fff;
    border: none; border-radius: 12px; font-size: 1.1rem; font-weight: 700;
    cursor: pointer; min-height: 64px; transition: background 0.2s, transform 0.1s;
  }
  .kiosk-ticket-done:hover { background: #6D28D9; }
  .kiosk-ticket-done:active { transform: scale(0.98); }

  .kiosk-error-toast {
    position: fixed; bottom: 2rem; left: 50%; transform: translateX(-50%);
    background: #991B1B; color: #FEE2E2; padding: 0.85rem 1.5rem;
    border-radius: 10px; font-size: 0.95rem; font-weight: 600;
    box-shadow: 0 8px 24px rgba(0,0,0,0.4); cursor: pointer; z-index: 110;
    animation: toast-fade 5s ease-in forwards;
  }
  @keyframes toast-fade { 0%,80% { opacity: 1; } 100% { opacity: 0; } }
`;
